// Datos de la pantalla Clientes: la tabla paginada, y la ficha y el historial del seleccionado.
import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ApiError, userMessageOf } from '@/api/errors';
import { customersBackofficeApi } from '@/api/services/retail-customer/backoffice';
import type { BackofficeCustomerPurchase } from '@/api/services/retail-customer/backoffice';
import { customersTableApi } from '@/api/services/retail-wizard/backoffice';
import {
    toListRows,
    type CreditReadState,
    type CustomerListRow,
    type CustomersServerFilters,
} from '../lib/customersList';
import { useLegacyCustomers } from './legacyCustomersList';

const KEY = ['retail-admin', 'clientes'] as const;

/** Techo de páginas del historial, para que un cursor que no avanza no pida para siempre. */
const MAX_PURCHASE_PAGES = 20;

/** Filas por página del servidor. */
export const CUSTOMERS_PAGE_SIZE = 50;

/** Páginas vacías seguidas que se piden solas antes de dejarle «Cargar más» al usuario. */
const MAX_EMPTY_PAGES_IN_A_ROW = 10;

/** Cuántas de las últimas páginas llegaron sin filas. */
export function trailingEmptyPages(pages: readonly { items: readonly unknown[] }[]): number {
    let count = 0;
    for (let i = pages.length - 1; i >= 0 && pages[i].items.length === 0; i--) count++;
    return count;
}

/** 404, o el 403 sin `code` Fiado con que API Gateway responde una ruta que no existe. */
export function isMissingEndpoint(error: unknown): boolean {
    if (!(error instanceof ApiError)) return false;
    return error.httpStatus === 404 || (error.httpStatus === 403 && !error.code);
}

export interface CustomersTableResult {
    rows: CustomerListRow[];
    /** `fallback` = el endpoint de tabla no existe todavía y se usa la lista vieja. */
    source: 'table' | 'fallback';
    creditState: CreditReadState;
    /** «Solo cuenta» del padrón leído, sumando los `counts` de cada página; `null` si no llegan. */
    accountOnlyCount: number | null;
    /** La lista trae el número de compras; el respaldo puede no traerlo. */
    hasAggregates: boolean;
    aggregatesUnavailableReason: string | null;
    windowFrom: string | null;
    truncated: boolean;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    /** Por qué falló la última página pedida; `null` si no falló. */
    nextPageError: string | null;
    fetchNextPage: () => void;
    isPending: boolean;
    isSuccess: boolean;
    /** Solo cuando no hay nada cargado: con páginas en mano, las filas se quedan. */
    error: unknown;
    refetch: () => void;
}

const NO_ROWS: CustomerListRow[] = [];

/** UNA llamada por página a `GET /backoffice/customers/table`; los filtros de servidor van en la key. */
export function useCustomersTable(filters: CustomersServerFilters): CustomersTableResult {
    const table = useInfiniteQuery({
        queryKey: [...KEY, 'table', filters],
        queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
            customersTableApi.getPage({
                from: filters.from ?? undefined,
                to: filters.to ?? undefined,
                storeId: filters.storeId ?? undefined,
                sellerId: filters.sellerId ?? undefined,
                customerType: filters.customerType ?? undefined,
                limit: CUSTOMERS_PAGE_SIZE,
                nextToken: pageParam,
            }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.nextToken ?? undefined,
        staleTime: 60_000,
        retry: (failures, error) => !isMissingEndpoint(error) && failures < 2,
    });

    // RESPALDO: se borra (con legacyCustomersList.ts) cuando el endpoint de tabla esté en dev.
    const useFallback = isMissingEndpoint(table.error);
    const legacy = useLegacyCustomers(filters, useFallback);

    const rows = useMemo(
        () => (table.data ? toListRows(table.data.pages.flatMap((page) => page.items)) : NO_ROWS),
        [table.data],
    );

    // El padrón puede devolver una página sin filas y con cursor: se sigue pidiendo solo, con tope.
    const emptyTail = trailingEmptyPages(table.data?.pages ?? []);
    const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = table;
    useEffect(() => {
        if (emptyTail === 0 || emptyTail >= MAX_EMPTY_PAGES_IN_A_ROW) return;
        if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) void fetchNextPage();
    }, [emptyTail, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

    if (useFallback) return legacy;

    const pages = table.data?.pages ?? [];
    const creditOk = pages.every((page) => page.sources.credit === 'OK');
    const accountOnlyCount = pages.every((page) => page.counts !== undefined)
        ? pages.reduce((sum, page) => sum + (page.counts?.accountOnly ?? 0), 0)
        : null;
    return {
        rows,
        source: 'table',
        accountOnlyCount,
        creditState: table.isPending ? 'loading' : creditOk ? 'ready' : 'error',
        hasAggregates: true,
        aggregatesUnavailableReason: null,
        windowFrom: pages.at(0)?.from ?? null,
        truncated: false,
        hasNextPage: table.hasNextPage,
        isFetchingNextPage: table.isFetchingNextPage,
        nextPageError: table.isFetchNextPageError ? userMessageOf(table.error) : null,
        fetchNextPage: () => {
            void table.fetchNextPage();
        },
        isPending: table.isPending,
        isSuccess: table.isSuccess,
        error: table.data ? null : table.error,
        refetch: () => {
            void table.refetch();
        },
    };
}

export function useCustomerDetail(retailCustomerId: string | null) {
    return useQuery({
        queryKey: [...KEY, 'detail', retailCustomerId],
        queryFn: () => customersBackofficeApi.getCustomer(retailCustomerId ?? ''),
        enabled: retailCustomerId !== null,
        staleTime: 60_000,
    });
}

export interface CustomerPurchasesResult {
    purchases: BackofficeCustomerPurchase[] | undefined;
    isPending: boolean;
    /** Siguen llegando páginas: los conteos todavía no son finales. */
    isLoadingMore: boolean;
    /** Se agotó el techo de páginas y el silo seguía ofreciendo más. */
    isTruncated: boolean;
    error: unknown;
    refetch: () => void;
}

/** Historial completo del cliente: encadena `nextToken` hasta el final. */
export function useCustomerPurchases(retailCustomerId: string | null): CustomerPurchasesResult {
    const query = useInfiniteQuery({
        queryKey: [...KEY, 'purchases', retailCustomerId],
        queryFn: ({ pageParam }) =>
            customersBackofficeApi.listPurchases(retailCustomerId ?? '', { nextToken: pageParam }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.nextToken ?? undefined,
        enabled: retailCustomerId !== null,
        staleTime: 60_000,
    });

    const cappedOut = (query.data?.pages.length ?? 0) >= MAX_PURCHASE_PAGES;
    const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

    useEffect(() => {
        if (hasNextPage && !isFetchingNextPage && !cappedOut) void fetchNextPage();
    }, [hasNextPage, isFetchingNextPage, cappedOut, fetchNextPage]);

    const purchases = useMemo(() => query.data?.pages.flatMap((page) => page.items), [query.data]);

    return {
        purchases,
        isPending: query.isPending,
        isLoadingMore: !query.isPending && hasNextPage && !cappedOut,
        isTruncated: cappedOut && hasNextPage,
        error: query.error,
        refetch: () => {
            void query.refetch();
        },
    };
}
